import { Component, OnInit } from "@angular/core";
import { ApiService } from "../../services";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { BaseComponent } from "../../shared/base.component";
import { Team } from "../../generated-models";
import { ToastrService } from "ngx-toastr";
import { forkJoin } from "rxjs";
import { IsServiceUser } from "../../utility/userprofile-helper";

@Component({
  standalone: false,
  templateUrl: "./teams-attest.component.html",
  styleUrls: ["../../styles.scss"],
})
export class TeamsAttestComponent extends BaseComponent implements OnInit {
  teamList: { label: string, value: string }[] = [];
  cloudNameList: { label: string, value: string }[] = [];
  requestSourceList = [
    { label: "EV2", value: "EV2" },
    { label: "CIS", value: "CIS" },
    { label: "Migrate To ARM", value: "Migrate To ARM" }
  ];

  serviceTreeId: string;
  cloudName: string;
  requestSource: string;
  validationError = "";

  constructor(
    private apiService: ApiService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService
  ) {
    super();
  }

  ngOnInit() {
    this.loadingService.setLoading(true);

    // Get user profile first to determine which teams to show
    this.apiService.getUserProfile().then(userProfile => {
      if (userProfile) {
        // Use forkJoin to wait for both API calls to complete
        forkJoin({
          teams: this.apiService.getTeamList(),
          regions: this.apiService.getRegionList(true)
        }).subscribe(
          ({ teams, regions }) => {
            // Process teams based on user permissions
            if (!teams) {
              console.warn("No teams obtained from api server.");
            } else {
              let filteredTeams: Team[] = [];

              // Filter teams based on user permissions
              if (userProfile.IsAdmin) {
                // Admins can see all teams
                filteredTeams = teams;
              } else {
                // Regular users can only see teams where they are a service User/Administrator,
                filteredTeams = teams.filter(team => IsServiceUser(userProfile, team.Oid));
              }

              // Format the team list to show both name and OID
              this.teamList = filteredTeams.map(team => ({
                label: `${team.Name} (${team.Oid})`,
                value: team.Oid
              }));
            }

            // Process regions/clouds
            if (!regions) {
              console.warn("No clouds obtained from api server.");
            } else {
              // Extract unique cloud names and filter out empty or null values
              this.cloudNameList = [...new Set(regions.map(region => region.CloudName))]
                .filter(cloudName => cloudName && cloudName.trim() !== "")
                .map(cloudName => ({
                  label: cloudName,
                  value: cloudName
                }));
            }

            this.loadingService.setLoading(false);
          },
          (error) => {
            console.error("Error loading data:", error);
            this.notificationService.error("Failed to load required data");
            this.loadingService.setLoading(false);
          }
        );
      } else {
        this.notificationService.error("User profile returns null, no data was loaded.");
        this.loadingService.setLoading(false);
      }
    }).catch(error => {
      this.notificationService.error(`Unable to get user profile, error: ${error}`);
      this.loadingService.setLoading(false);
    });
  }

  async submitAttest() {
    if (!this.serviceTreeId || !this.cloudName || !this.requestSource) {
      this.validationError = "All fields are required";
      return;
    }

    this.validationError = "";
    this.loadingService.setLoading(true);

    // Submit the attest data with the selected service tree ID
    return await this.apiService
      .addTeamAttest(this.serviceTreeId, this.cloudName, this.requestSource)
      .toPromise()
      .then(() => {
        const message = "Team attestation submitted successfully.";
        this.notificationService.info(message);
        // Reset form
        this.serviceTreeId = null;
        this.cloudName = null;
        this.requestSource = null;
        this.loadingService.setLoading(false);
      })
      .catch((error) => {
        const message = `Failed to submit team attestation, error: ${error}`;
        this.notificationService.error(message);
        this.loadingService.setLoading(false);
      });
  }
}
